/**
 * Multisucursal Fase 1B (V106/V107) — herramientas de inventario por sucursal:
 *   - <BranchTransfers/>: transferencias de stock entre sucursales (crear →
 *     recibir/cancelar) con reconciliación (la cantidad recibida puede diferir).
 *   - <BranchPricing/>: overrides de precio por sucursal sobre el catálogo
 *     COMPARTIDO (sin fila = precio de catálogo).
 *
 * COEXISTENCIA: ambos paneles se OCULTAN si el tenant tiene una sola sucursal
 * activa (un negocio de 1 sucursal no ve nada nuevo). El stock por sucursal en
 * las vistas de inventario ya sale del overlay del backend (sucursal activa).
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { BranchRow } from './BranchControls';

type Variant = { id: string; name: string; onHand?: number; available?: number };
type Product = { id: string; name: string; trackInventory: boolean; basePrice: number; variants?: Variant[] };
type Transfer = {
  id: string; status: 'in_transit' | 'received' | 'cancelled';
  qty: number; qtyReceived: number | null; note: string | null;
  createdAt: string; receivedAt: string | null;
  productName: string; variantId: string | null; variantName: string | null;
  fromBranchName: string; toBranchName: string;
};
type Override = { id: string; branchId: string; productId: string; variantId: string | null; serviceId: string | null; price: number };

const fmt = (n: number) => Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 });

/** Hook: sucursales activas + productos con inventario (compartido por ambos paneles). */
function useBranchInventoryData() {
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    api<BranchRow[]>('/branches').then((b) => setBranches(b.filter((x) => x.isActive))).catch(() => setBranches([]));
    api<Product[]>('/boutique/products').then(setProducts).catch(() => setProducts([]));
  }, []);
  return { branches, products };
}

/** Hook chico: ¿el tenant tiene más de una sucursal activa? (gatea los affordances). */
export function useHasMultipleBranches(): boolean {
  const [n, setN] = useState(0);
  useEffect(() => { api<BranchRow[]>('/branches').then((b) => setN(b.filter((x) => x.isActive).length)).catch(() => setN(0)); }, []);
  return n > 1;
}

/**
 * V106 — editor de precio por sucursal EN CONTEXTO de un producto (se abre desde
 * el editor de precio del producto). Muestra cada sucursal activa con su precio:
 * vacío = usa el catálogo; captura = override; limpiar = vuelve al catálogo.
 */
export function ProductBranchPriceModal({ productId, productName, basePrice, onClose }: {
  productId: string; productName: string; basePrice?: number; onClose: () => void;
}) {
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [existing, setExisting] = useState<Array<{ id: string; branchId: string; price: number }>>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    api<BranchRow[]>('/branches').then((b) => setBranches(b.filter((x) => x.isActive))).catch(() => setBranches([]));
    api<Array<{ id: string; branchId: string; price: number }>>(`/boutique/products/${productId}/branch-prices`)
      .then((o) => { setExisting(o); setDraft(Object.fromEntries(o.map((x) => [x.branchId, String(x.price)]))); })
      .catch(() => setExisting([]));
  };
  useEffect(() => { load(); }, [productId]);

  const save = async (branchId: string) => {
    const raw = draft[branchId]; setErr(null); setMsg(null);
    if (raw == null || raw.trim() === '' || !(Number(raw) >= 0)) { setErr('Captura un precio válido (o usa Limpiar).'); return; }
    setBusy(true);
    try { await api('/boutique/products/branch-pricing', { method: 'PUT', body: { branchId, productId, price: Number(raw) } }); setMsg('Precio guardado.'); load(); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo guardar.'); } finally { setBusy(false); }
  };
  const clear = async (branchId: string) => {
    const ov = existing.find((x) => x.branchId === branchId); if (!ov) { setDraft({ ...draft, [branchId]: '' }); return; }
    setBusy(true); setErr(null);
    try { await api(`/boutique/products/branch-pricing/${ov.id}`, { method: 'DELETE' }); setMsg('Volvió al precio de catálogo.'); load(); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo limpiar.'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Precio por sucursal</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <p className="mt-0.5 text-[12px] text-slate-500">{productName}{basePrice != null ? ` · catálogo $${fmt(basePrice)}` : ''}. Vacío = usa el catálogo.</p>
        {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
        {msg && <p className="mt-2 text-xs text-emerald-700">{msg}</p>}
        <div className="mt-3 space-y-2">
          {branches.map((b) => {
            const has = existing.some((x) => x.branchId === b.id);
            return (
              <div key={b.id} className="flex items-center gap-2">
                <span className="w-32 shrink-0 truncate text-sm text-slate-700">{b.name}{b.isDefault ? ' ★' : ''}</span>
                <input className="input flex-1" type="number" min="0" step="0.01"
                  placeholder={basePrice != null ? `catálogo ${fmt(basePrice)}` : 'precio'}
                  value={draft[b.id] ?? ''} onChange={(e) => setDraft({ ...draft, [b.id]: e.target.value })} />
                <button disabled={busy} onClick={() => save(b.id)} className="rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Fijar</button>
                <button disabled={busy || !has} onClick={() => clear(b.id)} className="rounded-lg px-2 py-1.5 text-xs font-medium text-rose-600 disabled:opacity-30">Limpiar</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function BranchTransfers() {
  const { branches, products } = useBranchInventoryData();
  const tracked = useMemo(() => products.filter((p) => p.trackInventory), [products]);
  const [rows, setRows] = useState<Transfer[]>([]);
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [productId, setProductId] = useState(''); const [variantId, setVariantId] = useState(''); const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null);

  // PR-3 — variantes del producto elegido; si tiene, la talla es obligatoria.
  const selProduct = useMemo(() => tracked.find((p) => p.id === productId), [tracked, productId]);
  const variants = selProduct?.variants ?? [];

  const load = () => api<Transfer[]>('/boutique/products/transfers/list').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  if (branches.length < 2) return null; // coexistencia: 1 sucursal = sin transferencias

  const create = async () => {
    setErr(null); setMsg(null);
    if (!from || !to || !productId || !(Number(qty) > 0)) { setErr('Completa origen, destino, producto y cantidad.'); return; }
    if (from === to) { setErr('Origen y destino deben ser distintos.'); return; }
    if (variants.length > 0 && !variantId) { setErr('Elige la talla/variante a transferir.'); return; }
    setBusy(true);
    try {
      await api('/boutique/products/transfers', { method: 'POST', body: {
        fromBranchId: from, toBranchId: to, productId,
        ...(variants.length > 0 ? { variantId } : {}), qty: Number(qty),
      } });
      setMsg('Transferencia creada (en tránsito).'); setQty(''); setProductId(''); setVariantId(''); await load();
    } catch (e: any) { setErr(e?.message ?? 'No se pudo crear la transferencia.'); }
    finally { setBusy(false); }
  };
  const receive = async (t: Transfer) => {
    const input = window.prompt(`¿Cuántas unidades llegaron? (enviadas: ${fmt(t.qty)})`, String(t.qty));
    if (input == null) return;
    const q = Number(input);
    if (!(q >= 0) || q > t.qty) { setErr('Cantidad recibida inválida (0 a lo enviado).'); return; }
    setBusy(true); setErr(null);
    try { await api(`/boutique/products/transfers/${t.id}/receive`, { method: 'POST', body: { qtyReceived: q } }); setMsg('Recepción registrada.'); await load(); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo recibir.'); } finally { setBusy(false); }
  };
  const cancel = async (t: Transfer) => {
    if (!window.confirm('¿Cancelar la transferencia y devolver el stock al origen?')) return;
    setBusy(true); setErr(null);
    try { await api(`/boutique/products/transfers/${t.id}/cancel`, { method: 'POST', body: {} }); setMsg('Transferencia cancelada.'); await load(); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo cancelar.'); } finally { setBusy(false); }
  };

  const badge = (s: Transfer['status']) => s === 'in_transit'
    ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">En tránsito</span>
    : s === 'received' ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Recibida</span>
    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">Cancelada</span>;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-800">Transferencias de inventario</h3>
      <p className="mt-0.5 text-[12px] text-slate-500">Mueve stock de una sucursal a otra. El destino confirma lo que llegó (reconciliación de merma).</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-5">
        <select className="input" value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">Origen…</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.isDefault ? ' ★' : ''}</option>)}
        </select>
        <select className="input" value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">Destino…</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.isDefault ? ' ★' : ''}</option>)}
        </select>
        <select className="input sm:col-span-2" value={productId} onChange={(e) => { setProductId(e.target.value); setVariantId(''); }}>
          <option value="">Producto…</option>{tracked.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input className="input" type="number" min="0" step="0.01" placeholder="Cantidad" value={qty} onChange={(e) => setQty(e.target.value)} />
      </div>
      {variants.length > 0 && (
        <div className="mt-2">
          <select className="input w-full sm:w-64" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
            <option value="">Talla / variante…</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>{v.name}{v.available != null ? ` · ${fmt(v.available)} disp.` : ''}</option>
            ))}
          </select>
        </div>
      )}
      <div className="mt-2 flex items-center gap-3">
        <button disabled={busy} onClick={create} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Transferir</button>
        {err && <span className="text-xs text-rose-600">{err}</span>}
        {msg && <span className="text-xs text-emerald-700">{msg}</span>}
      </div>
      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-400"><tr>
              <th className="py-1.5">Producto</th><th>Ruta</th><th>Enviado</th><th>Recibido</th><th>Estado</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="py-1.5 font-medium text-slate-700">{t.productName}{t.variantName ? <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{t.variantName}</span> : null}</td>
                  <td className="text-slate-500">{t.fromBranchName} → {t.toBranchName}</td>
                  <td className="tabular-nums">{fmt(t.qty)}</td>
                  <td className="tabular-nums">{t.qtyReceived != null ? fmt(t.qtyReceived) : '—'}</td>
                  <td>{badge(t.status)}</td>
                  <td className="text-right">
                    {t.status === 'in_transit' && (
                      <span className="flex justify-end gap-2">
                        <button disabled={busy} onClick={() => receive(t)} className="font-medium text-emerald-700 hover:underline">Recibir</button>
                        <button disabled={busy} onClick={() => cancel(t)} className="font-medium text-rose-600 hover:underline">Cancelar</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function BranchPricing() {
  const { branches, products } = useBranchInventoryData();
  const [branchId, setBranchId] = useState('');
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [productId, setProductId] = useState(''); const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const prodName = useMemo(() => new Map(products.map((p) => [p.id, p.name])), [products]);

  const load = (bid: string) => { if (bid) api<Override[]>(`/boutique/products/branch-pricing/${bid}`).then(setOverrides).catch(() => setOverrides([])); };
  useEffect(() => { load(branchId); }, [branchId]);

  if (branches.length < 2) return null; // coexistencia: 1 sucursal = sin overrides

  const add = async () => {
    setErr(null);
    if (!branchId || !productId || !(Number(price) >= 0)) { setErr('Elige sucursal, producto y precio.'); return; }
    setBusy(true);
    try { await api('/boutique/products/branch-pricing', { method: 'PUT', body: { branchId, productId, price: Number(price) } }); setProductId(''); setPrice(''); load(branchId); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo guardar el precio.'); } finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setBusy(true); setErr(null);
    try { await api(`/boutique/products/branch-pricing/${id}`, { method: 'DELETE' }); load(branchId); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo quitar.'); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-800">Precios por sucursal</h3>
      <p className="mt-0.5 text-[12px] text-slate-500">Precio distinto en una sucursal (ubicación premium). Sin override = precio del catálogo.</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
        <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
          <option value="">Sucursal…</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.isDefault ? ' ★' : ''}</option>)}
        </select>
        <select className="input sm:col-span-2" value={productId} onChange={(e) => setProductId(e.target.value)} disabled={!branchId}>
          <option value="">Producto…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex gap-2">
          <input className="input flex-1" type="number" min="0" step="0.01" placeholder="Precio" value={price} onChange={(e) => setPrice(e.target.value)} disabled={!branchId} />
          <button disabled={busy || !branchId} onClick={add} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Fijar</button>
        </div>
      </div>
      {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
      {branchId && (
        <div className="mt-4">
          {overrides.length === 0 ? <p className="text-xs text-slate-400">Sin precios especiales en esta sucursal (usa el catálogo).</p> : (
            <table className="w-full text-left text-xs">
              <thead className="text-slate-400"><tr><th className="py-1.5">Producto</th><th>Precio sucursal</th><th></th></tr></thead>
              <tbody>
                {overrides.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="py-1.5 font-medium text-slate-700">{o.productId ? (prodName.get(o.productId) ?? o.productId) : (o.variantId ? 'Variante' : 'Servicio')}</td>
                    <td className="tabular-nums">${fmt(o.price)}</td>
                    <td className="text-right"><button disabled={busy} onClick={() => remove(o.id)} className="font-medium text-rose-600 hover:underline">Quitar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
